import { z } from 'zod';

/**
 * =========================
 * Event INPUT (LLM emits)
 * =========================
 */

export const BaseChatEventInput = z.object({
  eventType: z.literal('chat'),
  messageId: z.string().optional(),
});

export const TextDeltaEventInput = BaseChatEventInput.extend({
  type: z.literal('text_delta'),
  delta: z.string(),
});

export const MessageStartEventInput = BaseChatEventInput.extend({
  type: z.literal('message_start'),
});

export const MessageCompleteEventInput = BaseChatEventInput.extend({
  type: z.literal('message_complete'),
});

export const ToolCallEventInput = BaseChatEventInput.extend({
  type: z.literal('tool_call'),
  toolName: z.string(),
  toolCallId: z.string(),
  args: z.unknown(),
});

export const ToolResultEventInput = BaseChatEventInput.extend({
  type: z.literal('tool_result'),
  toolName: z.string(),
  toolCallId: z.string(),
  result: z.unknown(),
});

export const ErrorEventInput = BaseChatEventInput.extend({
  type: z.literal('error'),
  message: z.string(),
});

export const DoneEventInput = BaseChatEventInput.extend({
  type: z.literal('done'),
});

export const ChatEventInput = z.discriminatedUnion('type', [
  MessageStartEventInput,
  TextDeltaEventInput,
  MessageCompleteEventInput,
  ToolCallEventInput,
  ToolResultEventInput,
  ErrorEventInput,
  DoneEventInput,
]);

export type ChatEventInput = z.infer<typeof ChatEventInput>;

/**
 * =========================
 * FULL EVENT (persisted + streamed)
 * =========================
 */

export const BaseChatEvent = z.object({
  eventType: z.literal('chat'),
  eventId: z.string(),
  chatId: z.string(),
  messageId: z.string().optional(),
  timestamp: z.number().optional(),
});

export const TextDeltaEvent = BaseChatEvent.extend({
  type: z.literal('text_delta'),
  delta: z.string(),
});

export const MessageStartEvent = BaseChatEvent.extend({
  type: z.literal('message_start'),
});

export const MessageCompleteEvent = BaseChatEvent.extend({
  type: z.literal('message_complete'),
});

export const ToolCallEvent = BaseChatEvent.extend({
  type: z.literal('tool_call'),
  toolName: z.string(),
  toolCallId: z.string(),
  args: z.unknown(),
});

export const ToolResultEvent = BaseChatEvent.extend({
  type: z.literal('tool_result'),
  toolName: z.string(),
  toolCallId: z.string(),
  result: z.unknown(),
});

export const ErrorEvent = BaseChatEvent.extend({
  type: z.literal('error'),
  message: z.string(),
});

export const DoneEvent = BaseChatEvent.extend({
  type: z.literal('done'),
});

export const ChatEvent = z.discriminatedUnion('type', [
  MessageStartEvent,
  TextDeltaEvent,
  MessageCompleteEvent,
  ToolCallEvent,
  ToolResultEvent,
  ErrorEvent,
  DoneEvent,
]);

export type ChatEvent = z.infer<typeof ChatEvent>;

export const ChatEventEnvelope = z.object({
  id: z.string(),
  data: ChatEvent,
});

export type ChatEventEnvelope = z.infer<typeof ChatEventEnvelope>;
